import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, of } from 'rxjs';
import { map, retry, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  Device,
  DeviceAdminIndex,
  DeviceAdminSummary,
  DeviceMessage,
  DeviceUpdate,
  DeviceUpdateStatus,
} from '../_models/device';
import {
  DeviceIndexResponse,
  DeviceUpdateResponse,
} from '../_models/api-responses';
import { PaginationData } from '../_models/pagination-data.model';
import { DeviceSetupStatus } from '../_models/device-setup-status';
import { Module } from '../_models/module-enum';
import { httpOptions } from '../_helpers/utils';
import { AuthenticationService } from './authentication.service';
import { Laboratory } from '../_models/laboratory';
import { Experiment } from '../_models/experiment';
import { FluorescenceChannel } from '../_models/fluorescence-channel';
import { InformationBannerType } from '../_models/information-banner';

@Injectable({
  providedIn: 'root',
})
export class DeviceService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  httpOptions = httpOptions;
  selectedlaboratoryId: number;

  // Pagination and sort/filter observables for dashboard index view
  private readonly tableSort$ = new BehaviorSubject<{
    sortBy: string;
    sortDirection: string;
  }>(null);
  private readonly tableFilter$ = new BehaviorSubject<string[]>([]);
  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);
  private readonly createDeviceCache$ = new BehaviorSubject<Device>(null);

  // Device status polling
  public device$: Observable<Device[]>;

  private readonly stopPolling$: Subject<boolean> = new Subject<boolean>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Set up subscription to selected laboratory
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((laboratory: Laboratory) => {
        if (this.selectedlaboratoryId !== laboratory?.laboratoryId) {
          this.selectedlaboratoryId = laboratory?.laboratoryId;
        }
      });
  }

  onUpdateSort = (sort: { sortBy: string; sortDirection: string }): void => {
    this.tableSort$.next(sort);
  };

  onUpdateFilter = (filter: string[]): void => {
    this.tableFilter$.next(filter);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  onUpdateCreateDeviceCache = (createDevice: Device): void => {
    this.createDeviceCache$.next(createDevice);
  };

  public get filter(): string[] {
    return this.tableFilter$.value;
  }

  public get sort(): { sortBy: string; sortDirection: string } {
    return this.tableSort$.value;
  }

  public get paginationCache(): { pageNumber: number; pageSize: number } {
    if (this.pageNumber$.value !== null && this.pageSize$.value !== null) {
      return {
        pageNumber: this.pageNumber$.value,
        pageSize: this.pageSize$.value,
      };
    } else {
      return null;
    }
  }

  public get retrieveAndClearUnsavedDeviceCache(): Device {
    const cached = this.createDeviceCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.createDeviceCache$.next(null);
  }

  // HTTP GET /DEVICES
  getDevices(
    sortBy = 'DeviceStatusId',
    sortOrder = 'asc',
    pageSize = 5,
    pageNumber = 1,
    moduleList?: Module[],
    deviceStatusId: DeviceSetupStatus = null,
    isOffline: boolean = null,
    includeDeleted: boolean = null,
    search: string = null,
  ): Observable<DeviceIndexResponse> {
    let url = `${environment.apiUrl}/devices?laboratoryId=${this.selectedlaboratoryId}&`;
    if (moduleList?.length > 0) {
      moduleList.forEach((m) => {
        url = url + `Modules=${m}&`;
      });
    }
    url = url + `OrderBy=${sortBy}&OrderByDirection=${sortOrder}`;
    if (pageNumber && pageSize) {
      url = url + `&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    }
    if (deviceStatusId) {
      url = url + `&deviceStatus=${deviceStatusId}`;
    }
    if (isOffline !== null) {
      url = url + `&isOffline=${isOffline.toString()}`;
    }
    if (includeDeleted !== null) {
      url = url + `&includeDeleted=${includeDeleted.toString()}`;
    }
    if (search !== null) {
      url = url + `&search=${search}`;
    }

    return this.http
      .get<{
        devices: Device[];
        displayInformationBanner: boolean;
        informationBannerMessage: string;
        informationBannerType: InformationBannerType;
      }>(url, this.httpOptions)
      .pipe(
        map((response) => {
          // Set pagination data
          let paginationData: PaginationData | null = null;
          try {
            const rawPagination = response.headers.get('X-Pagination');
            paginationData = rawPagination
              ? (JSON.parse(rawPagination) as PaginationData)
              : null;
          } catch {
            paginationData = null;
          }
          const body = response.body;
          const deviceList: Device[] = [];
          if (response.status === 200 && body) {
            body.devices.forEach((d: Device) => {
              const device = new Device(
                d.id,
                d.name,
                d.laboratoryId,
                d.macAddress || null,
                d.hardwareVersion || null,
                d.installedSoftwareVersion || null,
                d.softwareWarning ?? null,
              );
              device.odCalibrationId = d.odCalibrationId;
              device.oxygenCalibrationId = d.oxygenCalibrationId;
              device.phCalibrationId = d.phCalibrationId;
              device.spargingCalibrationId = d.spargingCalibrationId;
              device.deviceStatusId = d.deviceStatusId;
              device.isOffline = d.isOffline;
              device.deviceModules = d.deviceModules;
              device.experiment = d.experiment;
              device.pumpTubeLength = d.pumpTubeLength;
              deviceList.push(device);
            });
          }
          return {
            paginationData,
            deviceList,
            displayInformationBanner: body?.displayInformationBanner ?? false,
            informationBannerMessage: body?.informationBannerMessage ?? '',
            informationBannerType: body?.informationBannerType ?? null,
          };
        }),
      );
  }

  // HTTP GET /DEVICES/ALL
  getAllDevices(
    sortBy = 'Name',
    sortOrder = 'asc',
    pageNumber = 1,
    pageSize = 5,
    moduleList?: Module[],
    includeDeleted?: boolean,
    search?: string,
  ): Observable<DeviceAdminIndex> {
    let url = `${environment.apiUrl}/devices/all?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageNumber=${pageNumber}&PageSize=${pageSize}`;
    if (moduleList?.length > 0) {
      moduleList.forEach((m) => {
        url += `&Modules=${m}`;
      });
    }
    if (includeDeleted != null) {
      url += `&includeDeleted=${includeDeleted.toString()}`;
    }
    if (search) {
      url += `&Search=${encodeURIComponent(search)}`;
    }
    return this.http.get<DeviceAdminSummary[]>(url, this.httpOptions).pipe(
      map((response) => {
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination') ?? '{}',
        ) as PaginationData;
        const devices = response.status === 200 && response.body ? response.body : [];
        return { paginationData, devices };
      }),
    );
  }

  // HTTP GET /DEVICE/{id}
  getDeviceById(id: string): Observable<Device> {
    return this.http.get<Device>(`${environment.apiUrl}/devices/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  // HTTP POST /DEVICE - returns the id of the newly created device
  createDevice(device: Device): Observable<number> {
    return this.http
      .post<number>(`${environment.apiUrl}/devices`, device, this.httpOptions)
      .pipe(
        map((response) => {
          if (response.body == null) {
            throw { status: response.status, message: 'Something went wrong', code: null };
          }
          return response.body;
        }),
      );
  }

  // HTTP PATCH /DEVICE/{id}
  updateDevice(device: DeviceUpdate): Observable<DeviceUpdateResponse | void> {
    return this.http
      .patch<Device>(
        `${environment.apiUrl}/devices/${device.id}`,
        device,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return {
                device: response.body,
                message: 'The device has been updated.',
              };
            case 201:
              return {
                message:
                  'Update device request is pending approval; the status can be checked on the approvals page.',
              };
            default:
              throw Error('Something went wrong');
          }
        }),
      );
  }

  // HTTP PATCH /DEVICE/{id}
  updateDeviceStatus(
    deviceId: string,
    deviceStatus: DeviceUpdateStatus,
  ): Observable<DeviceUpdateStatus> {
    return this.http
      .patch<DeviceUpdateStatus>(
        `${environment.apiUrl}/devices/${deviceId}`,
        deviceStatus,
      )
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  // Polling for device status
  getDeviceStatus(): Observable<Device[]> {
    return this.device$;
  }

  stopDevicePolling(): void {
    this.stopPolling$.next(true);
  }

  startDevicePolling(
    experimentId: number,
    deviceIdList: number[],
  ): Observable<{ experiment: Experiment; devices: Device[] }> {
    // Clear previous polling
    this.stopPolling$.next(true);

    const experimentUrl = `${environment.apiUrl}/experiments/${experimentId}`;
    let deviceUrl = `${environment.apiUrl}/devices/by-device-array?`;
    deviceIdList.forEach((id) => {
      deviceUrl += `deviceIds=${id}&`;
    });

    const poll$ = timer(0, 30000).pipe(
      switchMap(() =>
        // Fetch experiment first
        this.http.get<Experiment>(experimentUrl).pipe(
          switchMap((experiment: Experiment) =>
            // Then fetch device data
            this.http.get<Device[]>(deviceUrl).pipe(
              map((devices) => ({
                experiment,
                devices,
              })),
            ),
          ),
        ),
      ),
      retry(),
      takeUntil(this.stopPolling$),
      shareReplay(1),
    );

    return poll$;
  }

  // HTTP PUT /device/deviceId?deviceId=1&isPausing=true/false
  pauseDeviceOnExperiment(
    deviceId: string,
    experimentId: number,
    isPausing: boolean,
  ): Observable<string> {
    return this.http
      .put<string>(
        `${
          environment.apiUrl
        }/devices/${deviceId}/experiment/${experimentId}?isPausing=${isPausing.toString()}`,
        null,
      )
      .pipe(
        map((response: string) => {
          return response;
        }),
      );
  }

  // HTTP DELETE /DEVICE/{id}
  deleteDevice(id: string): Observable<DeviceUpdateResponse> {
    return this.http
      .delete<void>(`${environment.apiUrl}/devices/${id}`, this.httpOptions)
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return {
                message: 'Device deleted.',
              };
            case 201:
              return {
                message:
                  'Delete device request is pending approval; the status can be checked on the approvals page.',
              };
            default:
              throw Error('Something went wrong');
          }
        }),
      );
  }

  // HTTP GET /DEVICE/{id}/command-device-request
  submitDeviceMessage(id: string, message: DeviceMessage): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiUrl}/devices/${id}/command-device-request`,
        message,
      )
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  // HTTP GET /DEVICE/{id}/command-device-report
  getDeviceMessages(id: string): Observable<DeviceMessage[]> {
    return this.http
      .get<
        DeviceMessage[]
      >(`${environment.apiUrl}/devices/${id}/command-device-report`)
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  // HTTP GET /DEVICE/{id}/CHANNELS
  getFluorescenceChannels(): Observable<FluorescenceChannel[]> {
    let channels: FluorescenceChannel[] = [];
    channels.push(
      {
        channelName: 'CFP',
        lowerBand: 455,
        upperBand: 540,
        led: 1,
        memorySlot: 0,
      },
      {
        channelName: 'DAPI',
        lowerBand: 425,
        upperBand: 550,
        led: 1,
        memorySlot: 1,
      },
      {
        channelName: 'DsRed',
        lowerBand: 545,
        upperBand: 630,
        led: 3,
        memorySlot: 2,
      },
      {
        channelName: 'EGFP',
        lowerBand: 510,
        upperBand: 550,
        led: 3,
        memorySlot: 3,
      },
      {
        channelName: 'mCherry',
        lowerBand: 625,
        upperBand: 680,
        led: 2,
        memorySlot: 4,
      },
      {
        channelName: 'YFP',
        lowerBand: 520,
        upperBand: 560,
        led: 3,
        memorySlot: 5,
      },
    );

    return of(channels);
  }

  ngOnDestroy(): void {
    this.stopPolling$.next(true);
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
